import React, { useState, useEffect } from "react";
import axios from "axios";
import { SERVER_URL } from "../../constants";
import { ClipLoader } from "react-spinners";
import { Trash, AlertCircle, Heart, LogIn } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";

const FavPage = () => {
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user, isLoaded } = useUser();
  const userId = user?.id;

  const fetchFavourites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER_URL}/api/v1/favi/${userId}`);
      setFavourites(response.data.cart || []);
    } catch (error) {
      console.error("Error fetching favourites:", error);
      toast.error("Failed to load favourite products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchFavourites();
    }
  }, [userId, refreshKey]);

  const handleRemoveFavourite = async (productId) => {
    try {
      setLoading(true);
      await axios.delete(`${SERVER_URL}/api/v1/favi/remove/${productId}/${userId}`);
      
      setFavourites((prev) => prev.filter((product) => product._id !== productId));
      
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
      }, 1000);

      toast.success("Product removed from favorites");
    } catch (error) {
      console.error("Error removing favourite product:", error);
      toast.error("Failed to remove product from favorites");
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="mt-40 flex justify-center items-center">
        <ClipLoader size={40} color="#2563eb" />
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="mt-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="p-4 bg-white rounded-full shadow-md">
              <Heart className="w-12 h-12 text-red-500" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-800">
              Your Favorites Await!
            </h2>
            
            <p className="text-lg text-gray-600 max-w-md">
              Sign in to view and manage your favorite products. Save items you love and access them anytime.
            </p>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <AlertCircle className="w-4 h-4" />
              <span>Your favorites are saved across all your devices when you sign in</span>
            </div>

            <button 
              onClick={() => window.location.href = '/login'} 
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              <LogIn className="w-5 h-5" />
              <span>Sign in to View Favorites</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-10 container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-gray-50 rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
            Your Favourite Products
          </h2>
          {loading && (
            <ClipLoader size={24} color="#2563eb" />
          )}
        </div>

        {favourites.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg">
            <svg
              className="w-16 h-16 text-gray-400 mb-4"
              fill="none"
              strokeWidth="1.5"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z"
              />
            </svg>
            <p className="text-xl text-gray-600">You have no favourite products yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favourites.map((product) => (
              <div
                key={product._id}
                className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl"
              >
                <div className="relative aspect-w-16 aspect-h-9">
                  <img
                    src={product.image || "/api/placeholder/400/300"}
                    alt={product.name || "Product Image"}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = "/api/placeholder/400/300";
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-1">
                    {product.name || "Unknown Product"}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm line-clamp-2">
                    {product.description || "No description available."}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-blue-600">
                      ₹{product.price ? product.price.toFixed(2) : "N/A"}
                    </span>
                    <button
                      onClick={() => handleRemoveFavourite(product.productId)}
                      disabled={loading}
                      className="flex items-center gap-1 px-3 py-1 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors duration-200"
                    >
                      <Trash size={16} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FavPage;